<template>
  <div v-if="visible" class="mask" @click.self="onCancel">
    <div class="dialog dialog--shell dialog--create" @click.stop>
      <div class="dialog-head">
        <div>
          <h3 class="dialog-title">{{ dialogTitle }}</h3>
          <p v-if="detail" class="dlg-meta">
            记录 #{{ detail.id }} · {{ detail.formName || detail.formCode || '—' }}
          </p>
        </div>
        <button type="button" class="dialog-close" aria-label="关闭" @click="onCancel">×</button>
      </div>

      <div class="dialog-body">
        <p v-if="errorMsg" class="error">{{ errorMsg }}</p>
        <div v-if="detail" class="preview-meta">
          <div>登记人：{{ detail.submitterName || '—' }}</div>
          <div>提交：{{ formatTime(detail.createdAt) }}</div>
          <div v-if="detail.updatedAt">更新：{{ formatTime(detail.updatedAt) }}</div>
        </div>

        <div v-if="loading" class="muted state">加载中…</div>
        <div v-else class="edit-form-scroll">
          <div class="smart-box">
            <textarea
              v-model="pasteText"
              class="smart-ta"
              rows="3"
              placeholder="粘贴或输入文本，可智能识别姓名、手机、单位、地址"
            />
            <div class="smart-foot">
              <button type="button" class="btn btn-smart" @click="onPasteAndParse">粘贴并识别</button>
            </div>
          </div>

          <div v-for="g in fieldGroups" :key="g.key" class="edit-group">
            <h4 class="edit-group-title">{{ g.title }}</h4>
            <label v-for="f in g.fields" :key="f.fieldKey" class="edit-field">
              <span class="edit-label">
                {{ f.label || f.fieldKey }}
                <span v-if="f.required" class="req">*</span>
              </span>
              <textarea
                v-if="isMultiline(f)"
                v-model="values[f.fieldKey]"
                class="edit-textarea"
                rows="3"
                :placeholder="placeholderFor(f)"
              />
              <input
                v-else-if="isDateField(f)"
                v-model="values[f.fieldKey]"
                class="edit-input"
                type="date"
              />
              <input
                v-else
                v-model="values[f.fieldKey]"
                class="edit-input"
                :type="normType(f) === 'NUMBER' ? 'number' : 'text'"
                :placeholder="placeholderFor(f)"
              />
            </label>
          </div>
          <p v-if="!fieldGroups.length" class="muted">未加载到表单字段</p>
        </div>
      </div>

      <div v-if="!loading" class="dialog-foot">
        <button type="button" class="btn btn-cancel" :disabled="saving" @click="onCancel">取消</button>
        <button type="button" class="btn btn-primary" :disabled="saving || !fieldGroups.length" @click="save">
          {{ saving ? '保存中…' : '保存修改' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getForms,
  getFormFields,
  getFormGroups,
  getServiceFormSubmission,
  updateServiceFormSubmission,
} from '@/api'
import { applySmartPasteToFormValues, parseContactPasteText } from '@/utils/smartPasteParse.js'

const META_KEYS = new Set([
  'submitterId',
  'submitterName',
  'submitterMobile',
  'lastEditedAt',
  'lastEditorName',
  'lastEditorSource',
])

export default {
  name: 'ServiceFormEditDialog',
  props: {
    visible: { type: Boolean, default: false },
    submissionId: { type: [Number, String], default: 0 },
  },
  emits: ['update:visible', 'saved'],
  data() {
    return {
      loading: false,
      saving: false,
      errorMsg: '',
      detail: null,
      schemaFields: [],
      groups: [],
      values: {},
      pasteText: '',
    }
  },
  computed: {
    dialogTitle() {
      const name = this.detail && this.detail.formName ? String(this.detail.formName).trim() : ''
      return name ? `编辑${name}` : '编辑服务登记'
    },
    fieldGroups() {
      const list = Array.isArray(this.schemaFields) ? this.schemaFields : []
      const groupRows = Array.isArray(this.groups) ? this.groups : []
      const groupById = {}
      groupRows.forEach((g) => {
        if (!g || g.id == null) return
        groupById[String(g.id)] = String(g.groupName || '').trim() || '登记信息'
      })
      const map = {}
      list.forEach((f, idx) => {
        const gid = f.groupId != null ? String(f.groupId) : ''
        const title = groupById[gid] || '登记信息'
        const key = gid || title
        if (!map[key]) map[key] = { key: `${idx}-${key}`, title, fields: [] }
        map[key].fields.push(f)
      })
      return Object.values(map).map((g) => ({
        ...g,
        fields: g.fields.slice().sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0)),
      }))
    },
  },
  watch: {
    visible(v) {
      if (v) this.boot()
    },
    submissionId() {
      if (this.visible) this.boot()
    },
  },
  methods: {
    formatTime(v) {
      if (!v) return '—'
      const s = String(v).replace('T', ' ')
      return s.length > 19 ? s.slice(0, 19) : s
    },
    normType(f) {
      return String((f && f.fieldType) || 'TEXT').trim().toUpperCase()
    },
    isMultiline(f) {
      return this.normType(f) === 'TEXTAREA'
    },
    isDateField(f) {
      const t = this.normType(f)
      return t === 'DATE' || t === 'DATETIME'
    },
    placeholderFor(f) {
      const key = String((f && f.fieldKey) || '').trim()
      const hints = {
        memberPosition: '若不是会员则填"/"',
        visitors: '职务+姓名',
        receptionStaff: '职务+姓名',
        serviceRequirement: '若无则填"/"',
      }
      if (hints[key]) return hints[key]
      return `请填写${(f && f.label) || ''}`
    },
    onCancel() {
      this.$emit('update:visible', false)
    },
    async boot() {
      const id = Number(this.submissionId || 0)
      if (!id) {
        this.errorMsg = '缺少记录 ID'
        return
      }
      this.loading = true
      this.saving = false
      this.errorMsg = ''
      this.pasteText = ''
      try {
        const detail = await getServiceFormSubmission(id)
        this.detail = detail && typeof detail === 'object' ? detail : null
        const code = String((detail && detail.formCode) || '').trim()
        if (!code) throw new Error('记录缺少表单类型')
        const forms = await getForms()
        const form = (Array.isArray(forms) ? forms : []).find(
          (x) => String((x && x.code) || '').trim() === code,
        )
        if (!form || !form.id) throw new Error('后台未找到对应表单配置')
        const [fields, groups] = await Promise.all([
          getFormFields(form.id),
          getFormGroups(form.id),
        ])
        this.schemaFields = (Array.isArray(fields) ? fields : []).filter(
          (f) => f && String(f.statusCode || 'ENABLED').toUpperCase() === 'ENABLED',
        )
        this.groups = Array.isArray(groups) ? groups : []
        const payload = detail && detail.payload && typeof detail.payload === 'object' ? detail.payload : {}
        const v = {}
        this.schemaFields.forEach((f) => {
          v[f.fieldKey] = payload[f.fieldKey] != null ? String(payload[f.fieldKey]) : ''
        })
        if (payload.visitLatitude != null) v.visitLatitude = payload.visitLatitude
        if (payload.visitLongitude != null) v.visitLongitude = payload.visitLongitude
        this.values = v
      } catch (e) {
        this.errorMsg = (e && e.message) || '加载失败'
        this.detail = null
        this.schemaFields = []
        this.groups = []
        this.values = {}
      } finally {
        this.loading = false
      }
    },
    onPasteAndParse() {
      const text = String(this.pasteText || '').trim()
      if (!text) {
        window.alert('请先输入或粘贴内容')
        return
      }
      const parsed = parseContactPasteText(text)
      this.values = applySmartPasteToFormValues(this.schemaFields, this.values, parsed, { force: true })
      const filled = [parsed.name, parsed.phone, parsed.company, parsed.address].filter(Boolean).length
      window.alert(filled ? `已识别 ${filled} 项` : '未识别到有效信息，请手动填写')
    },
    buildPayload() {
      const payload = { ...(this.values || {}) }
      const lat = Number(payload.visitLatitude)
      const lng = Number(payload.visitLongitude)
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        payload.visitLatitude = lat
        payload.visitLongitude = lng
      } else {
        delete payload.visitLatitude
        delete payload.visitLongitude
      }
      const existing = (this.detail && this.detail.payload) || {}
      META_KEYS.forEach((k) => {
        if (existing[k] != null && payload[k] == null) payload[k] = existing[k]
      })
      return payload
    },
    async save() {
      const id = Number(this.submissionId || 0)
      if (!id || this.saving) return
      for (let i = 0; i < this.schemaFields.length; i += 1) {
        const f = this.schemaFields[i]
        if (!f.required) continue
        const v = this.values[f.fieldKey]
        if (!String(v || '').trim()) {
          window.alert(`请填写${f.label || f.fieldKey}`)
          return
        }
      }
      this.saving = true
      this.errorMsg = ''
      try {
        await updateServiceFormSubmission(id, this.buildPayload())
        this.$emit('saved')
        this.$emit('update:visible', false)
      } catch (e) {
        this.errorMsg = (e && e.message) || '保存失败'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.dlg-meta {
  margin: 0;
  font-size: 13px;
  color: #667085;
}
.preview-meta {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  font-size: 13px;
  color: #667085;
  margin-bottom: 10px;
}
.error {
  color: #d92d20;
  font-size: 13px;
  margin: 0 0 8px;
}
.state {
  padding: 24px 0;
  text-align: center;
}
.edit-form-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-right: 4px;
  padding-bottom: 12px;
}
.smart-box {
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid #e4ecff;
  border-radius: 10px;
  background: #f8faff;
}
.smart-ta {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  resize: vertical;
}
.smart-foot {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}
.btn-smart {
  border: none;
  border-radius: 999px;
  padding: 6px 16px;
  background: #ff4d4f;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}
.edit-group {
  margin-bottom: 16px;
}
.edit-group-title {
  margin: 0 0 10px;
  padding-left: 10px;
  border-left: 4px solid #2f6eff;
  font-size: 15px;
}
.edit-field {
  display: block;
  margin-bottom: 12px;
}
.edit-label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #344054;
  font-weight: 600;
}
.req {
  color: #d92d20;
  margin-left: 4px;
}
.edit-input,
.edit-textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
}
.edit-textarea {
  min-height: 80px;
  resize: vertical;
}
.btn {
  min-height: 36px;
  padding: 8px 18px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  cursor: pointer;
}
.btn-primary {
  background: #2f6af6;
  color: #fff;
}
.btn-cancel,
.btn-edit {
  background: #fff;
  border-color: #d0d5dd;
  color: #344054;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.muted {
  color: #667085;
}
</style>
